import React from 'react';
import { Document, Page, View, Text, Image, StyleSheet } from '@react-pdf/renderer';
import { format, parseISO } from 'date-fns';

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function formatDate(dateStr, fmt) {
  if (!dateStr) return '';
  try {
    const d = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr;
    if (fmt === 'DD/MM/YYYY') return format(d, 'dd/MM/yyyy');
    if (fmt === 'YYYY-MM-DD') return format(d, 'yyyy-MM-dd');
    if (fmt === 'DD-MMM-YYYY') return `${format(d, 'dd')}-${MONTH_ABBR[d.getMonth()]}-${format(d, 'yyyy')}`;
    return format(d, 'MM/dd/yyyy');
  } catch { return String(dateStr); }
}

const BANNER_COLORS = {
  black:  '#111',
  blue:   '#0d6efd',
  red:    '#dc3545',
  green:  '#198754',
  orange: '#fd7e14',
};

// 4×6 landscape: 432pt × 288pt
const W = 432;
const H = 288;
const PAD = 14; // 0.2in in points
const LABEL_COL = 90;                         // wide enough for "Expiration:"
const VALUE_W = W - PAD * 2 - LABEL_COL;     // usable width for field values

const styles = StyleSheet.create({
  page: {
    width: W,
    height: H,
    paddingHorizontal: PAD,
    paddingBottom: PAD,
    paddingTop: 0,
    flexDirection: 'column',
    backgroundColor: '#fff',
    fontFamily: 'Helvetica',
  },
  banner: {
    paddingVertical: 5,
    paddingHorizontal: PAD,
    marginHorizontal: -PAD,
    marginBottom: 6,
    textAlign: 'center',
  },
  bannerText: {
    fontSize: 11,
    fontFamily: 'Helvetica-Bold',
    color: '#fff',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  palletNumber: {
    fontSize: 34,
    fontFamily: 'Helvetica-Bold',
    textAlign: 'center',
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  palletId: {
    fontSize: 10,
    color: '#555',
    textAlign: 'center',
    marginBottom: 8,
  },
  fieldRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginBottom: 13,
  },
  fieldLabel: {
    width: LABEL_COL,
    flexShrink: 0,
    fontSize: 12,
    fontFamily: 'Helvetica-Bold',
    color: '#333',
    paddingBottom: 2,
  },
  fieldValueWrap: {
    width: VALUE_W,
    flexShrink: 0,
    borderBottomWidth: 1,
    borderBottomColor: '#999',
    paddingBottom: 2,
  },
  fieldValue: {
    fontSize: 14,
  },
  fieldValueBold: {
    fontSize: 15,
    fontFamily: 'Helvetica-Bold',
  },
  bottomRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginTop: 6,
  },
  mfgBlock: {
    flex: 1,
  },
  mfgTitle: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: '#333',
    marginBottom: 1,
  },
  mfgName: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: '#333',
  },
  mfgAddress: {
    fontSize: 7,
    color: '#444',
  },
  logoBlock: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  casesBlock: {
    flex: 1,
    alignItems: 'flex-end',
  },
  casesBox: {
    borderWidth: 2,
    borderColor: '#000',
    borderRadius: 3,
    paddingVertical: 4,
    paddingHorizontal: 8,
    alignItems: 'center',
    minWidth: 64,
  },
  casesLabelText: {
    fontSize: 6,
    fontFamily: 'Helvetica-Bold',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  casesValue: {
    fontSize: 26,
    fontFamily: 'Helvetica-Bold',
    lineHeight: 1,
  },
  footer: {
    fontSize: 7,
    color: '#666',
    textAlign: 'center',
    marginTop: 3,
  },
});

// Helvetica average char width ≈ fontSize × 0.62
function scaleFont(text, base) {
  const len = String(text || '').length;
  if (len === 0) return base;
  const maxByWidth = VALUE_W / (len * 0.62);
  return Math.min(base, Math.max(10, maxByWidth));
}

function PdfLabel({ pallet, bol, template, companyName, companyAddress, logoUrl }) {
  const t = template || {};
  const bannerColor = BANNER_COLORS[t.storageBannerColor] || BANNER_COLORS.black;
  const dateLabel = t.dateLabel || 'Expiration';
  const casesLabel = t.casesLabel || 'CASES ON PALLET';
  const showLogo = t.showLogo !== false;
  const showMfg = t.showManufacturedBy !== false;
  const expirationDisplay = pallet.expirationDate ? formatDate(pallet.expirationDate, t.dateFormat) : '';
  const poDisplay = bol?.customerPO || bol?.poNumber || '';

  return (
    <Page size={[W, H]} style={styles.page}>

      {/* Banner */}
      {t.storageBanner ? (
        <View style={[styles.banner, { backgroundColor: bannerColor }]}>
          <Text style={styles.bannerText}>{t.storageBanner}</Text>
        </View>
      ) : (
        <View style={{ height: PAD }} />
      )}

      {/* Pallet number */}
      <Text style={styles.palletNumber}>PALLET {pallet.palletNumber} / {pallet.totalPallets}</Text>
      <Text style={styles.palletId}>ID: {pallet.palletId}</Text>

      {/* Fields */}
      <View>
        <FieldRow label="PO #"      value={poDisplay} />
        <FieldRow label="Lot(s)"    value={pallet.lotCodes?.join(', ') || ''} />
        <FieldRow label="Product"   value={pallet.products?.join(' / ') || ''} />
        <FieldRow label={dateLabel} value={expirationDisplay} bold />
      </View>

      {/* Push bottom row to the foot of the label */}
      <View style={{ flex: 1 }} />

      {/* Bottom row */}
      <View style={[styles.bottomRow, { marginTop: 0 }]}>
        <View style={styles.mfgBlock}>
          {showMfg && (
            <>
              <Text style={styles.mfgTitle}>Manufactured by:</Text>
              <Text style={styles.mfgName}>{companyName}</Text>
              <Text style={styles.mfgAddress}>{companyAddress}</Text>
            </>
          )}
        </View>

        <View style={styles.logoBlock}>
          {showLogo && logoUrl && (
            <Image src={logoUrl} style={{ height: 38, objectFit: 'contain' }} />
          )}
        </View>

        <View style={styles.casesBlock}>
          <View style={styles.casesBox}>
            <Text style={styles.casesLabelText}>{casesLabel}</Text>
            <Text style={styles.casesValue}>{pallet.cases ?? 0}</Text>
          </View>
        </View>
      </View>

      {t.customFooterNote ? <Text style={styles.footer}>{t.customFooterNote}</Text> : null}
    </Page>
  );
}

function FieldRow({ label, value, bold }) {
  const clean = String(value || '').replace(/[\r\n]+/g, ', ').trim();
  const fontSize = scaleFont(clean, bold ? 15 : 14);
  return (
    <View style={styles.fieldRow}>
      <Text style={styles.fieldLabel}>{label}:</Text>
      <View style={styles.fieldValueWrap}>
        <Text style={bold ? [styles.fieldValueBold, { fontSize }] : [styles.fieldValue, { fontSize }]}>
          {clean}
        </Text>
      </View>
    </View>
  );
}

export default function PalletLabelPdf({ pallets, bol, template, companyName, companyAddress, logoUrl }) {
  const totalPallets = pallets.length;
  return (
    <Document>
      {pallets.map(p => (
        <PdfLabel
          key={p.palletId}
          pallet={{ ...p, totalPallets }}
          bol={bol}
          template={template}
          companyName={companyName}
          companyAddress={companyAddress}
          logoUrl={logoUrl}
        />
      ))}
    </Document>
  );
}
